import { isFinite } from "lodash"

import { IGetArticleDto, Article, ICreateArticleDto } from "./Article"
import { Guid, ITranslations } from "@/types"
import ArticleService from "services/ArticleService"
import { prepareTranslations } from "utils/translations"
import { ArticleClass } from "./ArticleClassInfo"

export interface IGetArticleEquipmentDto extends IGetArticleDto {
    articleClass: ArticleClass
    description: string
    descriptionTranslations: ITranslations
    features?: ITranslations | null
    measurementSystemId: string
    volume?: number | null
    weight?: number | null
    width?: number | null
    height?: number | null
    length?: number | null
    inch?: number | null
    wheelWidth?: number | null
    wheelHeight?: number | null
    loadSpeedIndex?: string | null
    constructionCode?: string | null
    isTubeless?: boolean | null
    starMarking?: number | null
    quickCouplingSystem?: string | null
    connectionType?: string | null
    teeth?: boolean | null
    cuttingEdge?: boolean | null
    heavyDuty?: boolean | null
    locking?: string | null
    controlSystem?: string | null
    agility?: string | null
}

export interface ICreateArticleEquipmentDto extends ICreateArticleDto {
    articleClass: ArticleClass
    descriptionTranslations: ITranslations
    features?: ITranslations
    measurementSystemId: string
    volume?: number
    weight?: number
    width?: number
    height?: number
    length?: number
    inch?: number
    wheelWidth?: number
    wheelHeight?: number
    loadSpeedIndex?: string
    constructionCode?: string
    isTubeless?: boolean
    starMarking?: number
    quickCouplingSystem?: string | null
    connectionType?: string | null
    teeth?: boolean | null
    cuttingEdge?: boolean | null
    heavyDuty?: boolean | null
    locking?: string | null
    controlSystem?: string | null
    agility?: string | null
    sequence?: number | null
}

export interface IUpdateArticleEquipmentBaseDTO {
    articleClass?: ArticleClass
    measurementSystemId?: Guid
    width?: number | null
    height?: number | null
    weight?: number | null
    volume?: number | null
    length?: number | null
    wheelWidth?: number | null
    wheelHeight?: number | null
    inch?: number | null
    loadSpeedIndex?: string | null
    constructionCode?: string | null
    isTubeless?: boolean | null
    starMarking?: number | null
    highLevelArticleNumber?: Guid | null
    lowLevelArticleNumbers?: Guid[] | null
    inspectionRequired?: boolean
    quickCouplingSystem?: string | null
    connectionType?: string | null
    teeth?: boolean | null
    cuttingEdge?: boolean | null
    heavyDuty?: boolean | null
    locking?: string | null
    controlSystem?: string | null
    agility?: string | null
    sequence?: number | null
}

export interface IUpdateArticleEquipmentDTO extends IUpdateArticleEquipmentBaseDTO {
    descriptionTranslations?: ITranslations
    features?: ITranslations
}

export class ArticleEquipment extends Article {
    articleClass: ArticleClass
    description: string
    descriptionTranslations: ITranslations
    features?: ITranslations
    measurementSystemId: string
    volume?: number
    weight?: number
    width?: number
    height?: number
    length?: number
    inch?: number
    wheelWidth?: number
    wheelHeight?: number
    loadSpeedIndex?: string
    constructionCode?: string
    isTubeless?: boolean
    starMarking?: number
    quickCouplingSystem?: string
    connectionType?: string
    teeth?: boolean | null
    cuttingEdge?: boolean | null
    heavyDuty?: boolean | null
    locking?: string
    controlSystem?: string
    agility?: string
    sequence?: number

    constructor(dto: IGetArticleEquipmentDto) {
        super(dto)

        this.articleClass = dto.articleClass
        this.description = dto.description
        this.descriptionTranslations = dto.descriptionTranslations
        this.features = dto.features ?? undefined
        this.measurementSystemId = dto.measurementSystemId
        this.volume = dto.volume ?? undefined
        this.weight = dto.weight ?? undefined
        this.width = dto.width ?? undefined
        this.height = dto.height ?? undefined
        this.length = dto.length ?? undefined
        this.inch = dto.inch ?? undefined
        this.wheelWidth = dto.wheelWidth ?? undefined
        this.wheelHeight = dto.wheelHeight ?? undefined
        this.loadSpeedIndex = dto.loadSpeedIndex ?? undefined
        this.constructionCode = dto.constructionCode ?? undefined
        this.isTubeless = dto.isTubeless ?? undefined
        this.starMarking = dto.starMarking ?? undefined
        this.quickCouplingSystem = dto.quickCouplingSystem ?? undefined
        this.connectionType = dto.connectionType ?? undefined
        this.teeth = dto.teeth
        this.cuttingEdge = dto.cuttingEdge
        this.heavyDuty = dto.heavyDuty
        this.locking = dto.locking ?? undefined
        this.controlSystem = dto.controlSystem ?? undefined
        this.agility = dto.agility ?? undefined
        this.sequence = dto.sequence ?? undefined
    }

    #toCreateDto(): ICreateArticleEquipmentDto {
        return {
            articleType: this.articleType,
            articleClass: this.articleClass,
            isExtManufacturer: this.isExtManufacturer,
            manufacturerDescription: this.manufacturerDescription,
            manufacturerArticleNo: this.manufacturerArticleNo,
            highLevelArticleNumber: this.highLevelArticleNumber,
            lowLevelArticleNumbers: this.lowLevelArticleNumbers,
            descriptionTranslations: prepareTranslations(this.descriptionTranslations),
            drawingNumber: this.drawingNumber,
            features: this.features ? prepareTranslations(this.features) : undefined,
            measurementSystemId: this.measurementSystemId,
            volume: this.volume,
            weight: this.weight,
            width: this.width,
            height: this.height,
            length: this.length,
            inch: this.inch,
            wheelWidth: this.wheelWidth,
            wheelHeight: this.wheelHeight,
            loadSpeedIndex: this.loadSpeedIndex,
            constructionCode: this.constructionCode,
            isTubeless: this.isTubeless,
            starMarking: this.starMarking,
            imageFileId: this.imageFileId,
            inspectionRequired: this.inspectionRequired,
            quickCouplingSystem: this.quickCouplingSystem,
            connectionType: this.connectionType,
            teeth: this.teeth,
            cuttingEdge: this.cuttingEdge,
            heavyDuty: this.heavyDuty,
            locking: this.locking,
            controlSystem: this.controlSystem,
            agility: this.agility,
            sequence: this.sequence,
        }
    }

    #toUpdateDto(): IUpdateArticleEquipmentDTO {
        return {
            articleClass: this.articleClass,
            highLevelArticleNumber: this.highLevelArticleNumber,
            lowLevelArticleNumbers: this.lowLevelArticleNumbers,
            descriptionTranslations: prepareTranslations(this.descriptionTranslations),
            features: this.features ? prepareTranslations(this.features) : undefined,
            measurementSystemId: this.measurementSystemId,
            width: isFinite(this.width) ? this.width : null,
            height: isFinite(this.height) ? this.height : null,
            weight: isFinite(this.weight) ? this.weight : null,
            volume: isFinite(this.volume) ? this.volume : null,
            length: isFinite(this.length) ? this.length : null,
            wheelWidth: isFinite(this.wheelWidth) ? this.wheelWidth : null,
            wheelHeight: isFinite(this.wheelHeight) ? this.wheelHeight : null,
            inch: isFinite(this.inch) ? this.inch : null,
            loadSpeedIndex: this.loadSpeedIndex || null,
            constructionCode: this.constructionCode || null,
            isTubeless: typeof this.isTubeless === "boolean" ? this.isTubeless : null,
            starMarking: isFinite(this.starMarking) ? this.starMarking : null,
            inspectionRequired: this.inspectionRequired,
            quickCouplingSystem: this.quickCouplingSystem,
            connectionType: this.connectionType,
            teeth: this.teeth,
            cuttingEdge: this.cuttingEdge,
            heavyDuty: this.heavyDuty,
            locking: this.locking,
            controlSystem: this.controlSystem,
            agility: this.agility,
            sequence: isFinite(this.sequence) ? this.sequence : null,
        }
    }

    create() {
        return ArticleService.createArticle(this.#toCreateDto())
    }

    async update() {
        if (!this.linkUpdate) {
            throw new Error("Update not allowed")
        }
        await ArticleService.updateArticle(this.linkUpdate, this.#toUpdateDto())
    }
}
